import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    Fragment,
    useState,
    useRef
} from "react";
import {
    type TextInputKeyPressEventData,
    TextInput as NativeTextInput,
    type NativeSyntheticEvent,
    type FocusEvent,
    type BlurEvent,
    Platform,
    View
} from "react-native";
import type INumericInputProps from "./type";
import {
    type NumericInputValidationResult,
    type NumericInputSpinButtonProps,
    type NumericInputStepDirection,
    type NumericInputInstance,
    type INumericInputRef
} from "./type";
import stylesheet, {
    getNumericInputDecimalSeparator,
    getNumericInputFractionDigits,
    getNumericInputSanitizedText,
    NUMERIC_INPUT_KEY_DIRECTIONS,
    getNumericInputStepperSize,
    getNumericInputAffixColor,
    getNumericInputLimitText,
    getNumericInputStepValue,
    formatNumericInputValue,
    getNumericInputKeyboard,
    parseNumericInputValue,
    joinNumericInputParts,
    getNumericInputType,
    useStyles
} from "./stylesheet";
import NumericInputStepButton from "./internal/stepButton";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    type RefForwardingComponent
} from "../../types";
import {
    type FieldInputAccessibilityType,
    type FieldInputRenderPropsType,
    type FieldActionType
} from "../fieldControl/type";
import {
    FieldVisibilityIcon,
    FieldClearIcon
} from "../fieldControl/icons";
import {
    getFieldSizeKey,
    getFieldSize
} from "../fieldControl/stylesheet";
import {
    warnOnce
} from "../../utils/development";
import {
    uuid
} from "../../utils";
import FieldControl from "../fieldControl";
import Text from "../text";

const getIsStepperResponder = () => {
    return true;
};

const NumericInput: RefForwardingComponent<INumericInputRef, INumericInputProps> = ({
    rightIcon: RightIconComponentProp,
    onChangeText: onChangeTextProp,
    spreadBehaviour = "baseline",
    isAutoKeyboardDismissOnBlur,
    isShowHideTextButton = true,
    isWorkWithDebouncer = false,
    rightIconAccessibilityLabel,
    isShowHintTextIcon = false,
    onKeyPress: onKeyPressProp,
    isMinWidthEnabled = true,
    icon: IconComponentProp,
    hintTextContainerStyle,
    iconAccessibilityLabel,
    contentContainerStyle,
    isShowStepper = false,
    onFocus: onFocusProp,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    onBlur: onBlurProp,
    customBorderColor,
    hideTextIconStyle,
    isDecimal = false,
    customTitleColor,
    rightIconOnPress,
    type = "neutral",
    value: valueProp,
    variant = "text",
    customIconColor,
    cleanIconStyle,
    clearIconStyle,
    customLocalize,
    isCleanEnabled,
    isClearEnabled,
    isShowSubTitle,
    rightIconStyle,
    defaultValue,
    hintTextIcon,
    initialValue,
    keyboardType,
    customColor,
    customTheme,
    iconOnPress,
    inputStyle,
    isOptional,
    validation,
    iconStyle,
    inputMode,
    maxLength,
    editable,
    hintText,
    readOnly,
    step = 1,
    subTitle,
    prefix,
    suffix,
    style,
    title,
    size,
    max,
    min,
    id,
    ...props
}, ref) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const inputRef = useRef<NumericInputInstance | null>(null);

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const onChangeTextRef = useRef(onChangeTextProp);

    const generatedID = useRef(uuid());

    const [
        value,
        setValue
    ] = useState(initialValue ?? defaultValue ?? "");

    const [
        isValueVisible,
        setIsValueVisible
    ] = useState(false);

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const inputID = id ?? generatedID.current;

    const prefixID = `${inputID}-prefix`;

    const suffixID = `${inputID}-suffix`;

    const currentValue = isWorkWithDebouncer ? value : valueProp ?? value;

    const isCurrentReadOnly = isReadOnly || readOnly === true || editable === false;

    const isCurrentClearEnabled = isClearEnabled ?? isCleanEnabled ?? false;

    const isStepperVisible = isShowStepper && !isCurrentReadOnly;

    const isClearActionAvailable = isCurrentClearEnabled && variant === "text" && !isDisabled && !isCurrentReadOnly;

    const isHideTextButtonVisible = variant === "hidden" && isShowHideTextButton;

    const isEndActionRequested = isCurrentClearEnabled || isHideTextButtonVisible || !!RightIconComponentProp;

    const isCleanIconStyleUsed = cleanIconStyle !== undefined;

    const isRightIconPressable = !!rightIconOnPress;

    const isIconPressable = !!iconOnPress;

    const isNegativeAllowed = min !== undefined && min < 0;

    const stepSize = step > 0 ? step : 1;

    const stepMin = min ?? 0;

    const decimalSeparator = getNumericInputDecimalSeparator({
        locale: activeLocale
    });

    const parsedValue = parseNumericInputValue({
        text: currentValue,
        isNegativeAllowed,
        decimalSeparator,
        isDecimal
    });

    const isMinReached = parsedValue !== undefined && parsedValue <= stepMin;

    const isMaxReached = parsedValue !== undefined && max !== undefined && parsedValue >= max;

    const isOutOfRange = parsedValue !== undefined && (min !== undefined && parsedValue < min || max !== undefined && parsedValue > max);

    const fieldType = getNumericInputType({
        type
    });

    const currentType = isOutOfRange && fieldType === "neutral" ? "danger" : fieldType;

    const currentSizeKey = getFieldSizeKey({
        isActionable: isStepperVisible || isCurrentClearEnabled && variant === "text" || isHideTextButtonVisible || isIconPressable || isRightIconPressable,
        pointerType,
        size
    });

    const currentSize = getFieldSize({
        size: currentSizeKey,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces
    });

    const stepperSize = getNumericInputStepperSize({
        touchTargets,
        currentSize,
        pointerType,
        radiuses,
        borders,
        spaces
    });

    const currentKeyboard = getNumericInputKeyboard({
        isNegativeAllowed,
        keyboardType,
        isDecimal,
        inputMode
    });

    const affixColor = getNumericInputAffixColor({
        isDisabled,
        colors
    });

    const {
        stepperDivider: stepperDividerDynamicStyle,
        stepperGroup: stepperGroupDynamicStyle
    } = useStyles({
        currentSize,
        borders,
        colors,
        spaces
    });

    useImperativeHandle(
        ref,
        () => ({
            updateValue,
            cleanText,
            clearText,
            focus,
            blur
        })
    );

    useEffect(() => {
        onChangeTextRef.current = onChangeTextProp;
    }, [
        onChangeTextProp
    ]);

    useEffect(() => {
        if(valueProp === undefined) {
            return;
        }

        setValue(valueProp);
    }, [
        valueProp
    ]);

    useEffect(() => {
        return () => {
            if(debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    useEffect(() => {
        if(type !== "default") {
            return;
        }

        warnOnce({
            message: "NumericInput: type \"default\" is deprecated and will be removed in the next major version. Use \"neutral\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(type !== "question") {
            return;
        }

        warnOnce({
            message: "NumericInput: type \"question\" is deprecated and will be removed in the next major version. Use type \"neutral\" with \"isShowHintTextIcon\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(isCleanEnabled === undefined) {
            return;
        }

        warnOnce({
            message: "NumericInput: \"isCleanEnabled\" is deprecated and will be removed in the next major version. Use \"isClearEnabled\" instead."
        });
    }, [
        isCleanEnabled
    ]);

    useEffect(() => {
        if(!isCleanIconStyleUsed) {
            return;
        }

        warnOnce({
            message: "NumericInput: \"cleanIconStyle\" is deprecated and will be removed in the next major version. Use \"clearIconStyle\" instead."
        });
    }, [
        isCleanIconStyleUsed
    ]);

    useEffect(() => {
        if(isAutoKeyboardDismissOnBlur === undefined) {
            return;
        }

        warnOnce({
            message: "NumericInput: \"isAutoKeyboardDismissOnBlur\" is deprecated and has no effect. A field that loses focus already closes the keyboard unless focus moves to another field."
        });
    }, [
        isAutoKeyboardDismissOnBlur
    ]);

    useEffect(() => {
        if(!isIconPressable || iconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "NumericInput: a pressable \"icon\" (iconOnPress) needs an \"iconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        iconAccessibilityLabel,
        isIconPressable
    ]);

    useEffect(() => {
        if(!isRightIconPressable || rightIconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "NumericInput: a pressable \"rightIcon\" (rightIconOnPress) needs a \"rightIconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        rightIconAccessibilityLabel,
        isRightIconPressable
    ]);

    useEffect(() => {
        if(!isStepperVisible || !isEndActionRequested) {
            return;
        }

        warnOnce({
            message: "NumericInput: the step buttons take the end of the field, so the clear button, the password toggle and \"rightIcon\" are not shown while \"isShowStepper\" is on."
        });
    }, [
        isEndActionRequested,
        isStepperVisible
    ]);

    const focus = () => {
        inputRef.current?.focus();
    };

    const blur = () => {
        inputRef.current?.blur();
    };

    const cancelPendingChange = () => {
        if(!debounceTimer.current) {
            return;
        }

        clearTimeout(debounceTimer.current);

        debounceTimer.current = null;
    };

    const notifyChange = (text: string) => {
        if(!isWorkWithDebouncer) {
            if(onChangeTextProp) {
                onChangeTextProp(text);
            }

            return;
        }

        cancelPendingChange();

        debounceTimer.current = setTimeout(() => {
            debounceTimer.current = null;

            if(onChangeTextRef.current) {
                onChangeTextRef.current(text);
            }
        }, durations.medium);
    };

    const commitValue = (text: string) => {
        setValue(text);

        notifyChange(text);
    };

    const runValidation = (text: string): NumericInputValidationResult => {
        const result: NumericInputValidationResult = {
            isValid: true
        };

        if(!validation) {
            return result;
        }

        result.isValid = validation(text, (correctedText) => {
            result.correctedText = correctedText;
        });

        return result;
    };

    const applyText = (text: string) => {
        if(text === currentValue) {
            return;
        }

        if(text === "") {
            commitValue(text);

            return;
        }

        const result = runValidation(text);

        if(result.correctedText !== undefined) {
            if(result.correctedText !== currentValue) {
                commitValue(result.correctedText);
            }

            return;
        }

        if(!result.isValid) {
            return;
        }

        commitValue(text);
    };

    const clearText = () => {
        cancelPendingChange();

        setValue("");

        if(onChangeTextProp) {
            onChangeTextProp("");
        }
    };

    const cleanText = () => {
        warnOnce({
            message: "NumericInput: ref method \"cleanText\" is deprecated and will be removed in the next major version. Use \"clearText\" instead."
        });

        clearText();
    };

    const updateValue = (text: string) => {
        setValue(text);
    };

    const onClearPress = () => {
        clearText();

        focus();
    };

    const onChangeText = (text: string) => {
        applyText(getNumericInputSanitizedText({
            isNegativeAllowed,
            decimalSeparator,
            isDecimal,
            maxLength,
            text
        }));
    };

    const handleStep = (direction: NumericInputStepDirection) => {
        const fractionDigits = getNumericInputFractionDigits({
            text: currentValue,
            decimalSeparator,
            step: stepSize,
            isDecimal
        });

        const nextText = formatNumericInputValue({
            value: getNumericInputStepValue({
                value: parsedValue,
                fractionDigits,
                step: stepSize,
                min: stepMin,
                direction,
                max
            }),
            decimalSeparator,
            fractionDigits
        });

        if(maxLength !== undefined && nextText.length > maxLength) {
            return;
        }

        applyText(nextText);
    };

    const onDecrementPress = () => {
        handleStep("decrement");
    };

    const onIncrementPress = () => {
        handleStep("increment");
    };

    const onKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if(onKeyPressProp) {
            onKeyPressProp(event);
        }

        const direction = NUMERIC_INPUT_KEY_DIRECTIONS[event.nativeEvent.key];

        if(!direction || !isStepperVisible || isDisabled) {
            return;
        }

        event.preventDefault();

        if(direction === "decrement" ? isMinReached : isMaxReached) {
            return;
        }

        handleStep(direction);
    };

    const onFocus = (event: FocusEvent) => {
        setIsFocused(true);

        if(onFocusProp) {
            onFocusProp(event);
        }
    };

    const onBlur = (event: BlurEvent) => {
        setIsFocused(false);

        if(onBlurProp) {
            onBlurProp(event);
        }
    };

    const toggleValueVisibility = () => {
        setIsValueVisible((previousState) => !previousState);
    };

    const getSubTitle = () => {
        if(isOptional) {
            return isRequired ? undefined : localize("is-optional");
        }

        if(!isShowSubTitle) {
            return undefined;
        }

        return subTitle ?? localize("is-optional");
    };

    const getLimitHintText = () => {
        if(min !== undefined && isMinReached) {
            return localize("numeric-input-min-limit", [
                getNumericInputLimitText({
                    locale: activeLocale,
                    value: min,
                    prefix,
                    suffix
                })
            ]);
        }

        if(max !== undefined && isMaxReached) {
            return localize("numeric-input-max-limit", [
                getNumericInputLimitText({
                    locale: activeLocale,
                    value: max,
                    prefix,
                    suffix
                })
            ]);
        }

        return undefined;
    };

    const getInputAccessibilityProps = (accessibilityProps: FieldInputAccessibilityType): FieldInputAccessibilityType => {
        if(!prefix && !suffix) {
            return accessibilityProps;
        }

        if(Platform.OS === "web") {
            return {
                ...accessibilityProps,
                "aria-describedby": joinNumericInputParts({
                    parts: [
                        accessibilityProps["aria-describedby"],
                        prefix ? prefixID : undefined,
                        suffix ? suffixID : undefined
                    ],
                    separator: " "
                })
            };
        }

        return {
            ...accessibilityProps,
            accessibilityHint: joinNumericInputParts({
                parts: [
                    accessibilityProps.accessibilityHint,
                    prefix,
                    suffix
                ],
                separator: ", "
            })
        };
    };

    const getSpinButtonProps = (): NumericInputSpinButtonProps => {
        if(Platform.OS !== "web" || !isStepperVisible) {
            return {};
        }

        return {
            "aria-valuemax": max !== undefined && Number.isFinite(max) ? max : undefined,
            "aria-valuemin": min !== undefined && Number.isFinite(min) ? min : undefined,
            "aria-valuenow": parsedValue,
            role: "spinbutton"
        };
    };

    const getStartAction = (): FieldActionType | undefined => {
        if(!IconComponentProp) {
            return undefined;
        }

        return {
            accessibilityLabel: iconAccessibilityLabel,
            icon: IconComponentProp,
            onPress: iconOnPress,
            style: iconStyle,
            customIconColor
        };
    };

    const getEndAction = (): FieldActionType | undefined => {
        if(isStepperVisible) {
            return undefined;
        }

        if(isClearActionAvailable && (currentValue.length > 0 || !RightIconComponentProp)) {
            return {
                style: clearIconStyle ?? cleanIconStyle,
                accessibilityLabel: localize("clear"),
                isVisible: currentValue.length > 0,
                isControllingInput: true,
                onPress: onClearPress,
                icon: FieldClearIcon
            };
        }

        if(isHideTextButtonVisible) {
            return {
                accessibilityLabel: localize("show-password"),
                onPress: toggleValueVisibility,
                icon: FieldVisibilityIcon,
                isPressed: isValueVisible,
                style: hideTextIconStyle,
                isControllingInput: true
            };
        }

        if(!RightIconComponentProp) {
            return undefined;
        }

        return {
            accessibilityLabel: rightIconAccessibilityLabel,
            icon: RightIconComponentProp,
            onPress: rightIconOnPress,
            style: rightIconStyle
        };
    };

    const renderAffix = (affix: string | undefined, affixID: string) => {
        if(!affix) {
            return null;
        }

        return <Text
            variant={currentSize.valueVariant}
            customTheme={customTheme}
            customColor={affixColor}
            style={stylesheet.affix}
            aria-hidden={true}
            numberOfLines={1}
            id={affixID}
        >
            {affix}
        </Text>;
    };

    const renderSteppers = () => {
        if(!isStepperVisible) {
            return null;
        }

        return <Fragment>
            {
                suffix ?
                    <View
                        style={[
                            stylesheet.stepperDivider,
                            stepperDividerDynamicStyle
                        ]}
                        aria-hidden={true}
                    />
                :
                    null
            }
            <View
                onStartShouldSetResponder={getIsStepperResponder}
                style={[
                    stylesheet.stepperGroup,
                    stepperGroupDynamicStyle
                ]}
            >
                <NumericInputStepButton
                    accessibilityLabel={localize("decrease")}
                    isDisabled={isDisabled || isMinReached}
                    iconSize={currentSize.iconSize}
                    onPress={onDecrementPress}
                    stepperSize={stepperSize}
                    customTheme={customTheme}
                    direction="decrement"
                    controlsID={inputID}
                />
                <NumericInputStepButton
                    accessibilityLabel={localize("increase")}
                    isDisabled={isDisabled || isMaxReached}
                    iconSize={currentSize.iconSize}
                    onPress={onIncrementPress}
                    stepperSize={stepperSize}
                    customTheme={customTheme}
                    direction="increment"
                    controlsID={inputID}
                />
            </View>
        </Fragment>;
    };

    const renderInput = ({
        style: inputDynamicStyle,
        placeholderTextColor,
        accessibilityProps
    }: FieldInputRenderPropsType) => {
        return <Fragment>
            {renderAffix(prefix, prefixID)}
            <NativeTextInput
                {...getInputAccessibilityProps(accessibilityProps)}
                {...getSpinButtonProps()}
                {...props}
                secureTextEntry={variant === "hidden" && !isValueVisible}
                editable={!isDisabled && !isCurrentReadOnly}
                keyboardType={currentKeyboard.keyboardType}
                placeholderTextColor={placeholderTextColor}
                inputMode={currentKeyboard.inputMode}
                underlineColorAndroid="transparent"
                onChangeText={onChangeText}
                onKeyPress={onKeyPress}
                style={[
                    inputStyle,
                    stylesheet.input,
                    inputDynamicStyle
                ]}
                value={currentValue}
                multiline={false}
                onFocus={onFocus}
                onBlur={onBlur}
                ref={inputRef}
            />
            {renderAffix(suffix, suffixID)}
            {renderSteppers()}
        </Fragment>;
    };

    const getHintText = () => {
        if(isOutOfRange && fieldType === "neutral") {
            return getLimitHintText() ?? hintText;
        }

        return hintText ?? getLimitHintText();
    };

    return <FieldControl
        isShowHintTextIcon={isShowHintTextIcon || type === "question"}
        hintTextContainerStyle={hintTextContainerStyle}
        isMinWidthEnabled={isMinWidthEnabled}
        customBorderColor={customBorderColor}
        controlStyle={contentContainerStyle}
        customTitleColor={customTitleColor}
        spreadBehaviour={spreadBehaviour}
        customLocalize={customLocalize}
        startAction={getStartAction()}
        isReadOnly={isCurrentReadOnly}
        hintTextIcon={hintTextIcon}
        endAction={getEndAction()}
        renderInput={renderInput}
        customColor={customColor}
        customTheme={customTheme}
        hintText={getHintText()}
        subTitle={getSubTitle()}
        isDisabled={isDisabled}
        isRequired={isRequired}
        onFocusRequest={focus}
        isFocused={isFocused}
        size={currentSizeKey}
        type={currentType}
        inputID={inputID}
        title={title}
        style={style}
    />;
};
export default forwardRef(NumericInput);
